import { useMemo, useState, type ReactNode } from 'react'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group'
import { Label } from '@/components/ui/label'
import {
  useClasses,
  useDepartments,
  useHierarchyLevel,
  useSubclasses,
} from '@/hooks/useHierarchy'
import { useSeasons } from '@/hooks/useSeasons'
import { formatDistributionCenterName } from '@/utils/formatters'
import {
  buildChartBars,
  buildTotalsByWeekRows,
  DC_OPTIONS,
  EVENT_OPTIONS,
  METRIC_OPTIONS,
  SNAPSHOT_BASE_OPTIONS,
  SOURCE_OPTIONS,
  type StoreCubeCartonFilterState,
} from './storeCubeCartonUpdateMockData'
import { StoreCubeCartonUpdateChart } from './StoreCubeCartonUpdateChart'
import { StoreCubeCartonUpdateTable } from './StoreCubeCartonUpdateTable'

const ALL = '__all__'

function FilterField({
  label,
  children,
}: {
  label: string
  children: ReactNode
}) {
  return (
    <div className="flex flex-col gap-1">
      <label className="text-[11px] font-medium uppercase tracking-wide text-muted-foreground">
        {label}
      </label>
      {children}
    </div>
  )
}

function SimpleSelect({
  value,
  onValueChange,
  options,
  loading,
  formatOption = option => option,
}: {
  value: string | null
  onValueChange: (v: string | null) => void
  options: string[]
  loading?: boolean
  formatOption?: (option: string) => string
}) {
  return (
    <Select
      value={value ?? ALL}
      onValueChange={v => onValueChange(v === ALL ? null : v)}
    >
      <SelectTrigger className="w-full bg-white">
        <SelectValue placeholder={loading ? 'Loading...' : 'All'} />
      </SelectTrigger>
      <SelectContent>
        <SelectItem value={ALL}>All</SelectItem>
        {options.map(o => (
          <SelectItem key={o} value={o}>
            {formatOption(o)}
          </SelectItem>
        ))}
      </SelectContent>
    </Select>
  )
}

interface IdOption {
  id: string
  label: string
}

function IdSelect({
  value,
  onValueChange,
  options,
  loading,
  disabled,
}: {
  value: string | null
  onValueChange: (v: string | null) => void
  options: IdOption[]
  loading?: boolean
  disabled?: boolean
}) {
  return (
    <Select
      value={value ?? ALL}
      onValueChange={v => onValueChange(v === ALL ? null : v)}
      disabled={disabled}
    >
      <SelectTrigger className="w-full bg-white">
        <SelectValue placeholder={loading ? 'Loading...' : 'All'} />
      </SelectTrigger>
      <SelectContent>
        <SelectItem value={ALL}>All</SelectItem>
        {options.map(o => (
          <SelectItem key={o.id} value={o.id}>
            {o.label}
          </SelectItem>
        ))}
      </SelectContent>
    </Select>
  )
}

export function StoreCubeCartonUpdateMockup() {
  const [snapshotBase, setSnapshotBase] = useState<string>('Latest')
  const [source, setSource] = useState<string | null>(null)
  const [season, setSeason] = useState<string | null>(null)
  const [event, setEvent] = useState<string | null>(null)
  const [dc, setDc] = useState<string | null>(null)
  const [metric, setMetric] = useState<string>('Cartons')
  const [divisionId, setDivisionId] = useState<string | null>(null)
  const [departmentId, setDepartmentId] = useState<string | null>(null)
  const [classId, setClassId] = useState<string | null>(null)
  const [subclassId, setSubclassId] = useState<string | null>(null)

  const { data: seasons, isLoading: seasonsLoading } = useSeasons()
  const { data: divisions, isLoading: divisionsLoading } =
    useHierarchyLevel('DIVISION')
  const { data: departmentsScoped, isLoading: departmentsScopedLoading } =
    useHierarchyLevel('DEPARTMENT', divisionId)
  const { data: departmentsAll, isLoading: departmentsAllLoading } =
    useDepartments()
  const { data: classes, isLoading: classesLoading } = useClasses(departmentId)
  const { data: subclasses, isLoading: subclassesLoading } =
    useSubclasses(classId)

  const departments = divisionId ? departmentsScoped : departmentsAll
  const departmentsLoading = divisionId
    ? departmentsScopedLoading
    : departmentsAllLoading

  const seasonOptions = (seasons ?? []).map(s => s.name)
  const divisionOptions: IdOption[] = (divisions ?? []).map(d => ({
    id: d.id,
    label: `${d.code} - ${d.name}`,
  }))
  const departmentOptions: IdOption[] = (departments ?? []).map(d => ({
    id: d.id,
    label: `${d.code} - ${d.name}`,
  }))
  const classOptions: IdOption[] = (classes ?? []).map(c => ({
    id: c.id,
    label: `${c.code} - ${c.name}`,
  }))
  const subclassOptions: IdOption[] = (subclasses ?? []).map(s => ({
    id: s.id,
    label: `${s.code} - ${s.name}`,
  }))

  const filters = useMemo<StoreCubeCartonFilterState>(
    () => ({
      snapshotBase,
      source,
      season,
      event,
      dc,
      metric,
      divisionId,
      departmentId,
      classId,
      subclassId,
    }),
    [
      snapshotBase,
      source,
      season,
      event,
      dc,
      metric,
      divisionId,
      departmentId,
      classId,
      subclassId,
    ]
  )

  const chartBars = useMemo(() => buildChartBars(filters), [filters])
  const totalsByWeek = useMemo(() => buildTotalsByWeekRows(filters), [filters])

  const handleDivisionChange = (v: string | null) => {
    setDivisionId(v)
    setDepartmentId(null)
    setClassId(null)
    setSubclassId(null)
  }

  const handleDepartmentChange = (v: string | null) => {
    setDepartmentId(v)
    setClassId(null)
    setSubclassId(null)
  }

  const handleClassChange = (v: string | null) => {
    setClassId(v)
    setSubclassId(null)
  }

  return (
    <div className="flex flex-col gap-4 bg-[#fafafa] p-6">
      {/* Filter row — 9 filters in one row, matching the screenshot */}
      <div className="grid grid-cols-9 gap-4">
        <FilterField label="Snapshot Base">
          <Select value={snapshotBase} onValueChange={setSnapshotBase}>
            <SelectTrigger className="w-full bg-white">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {SNAPSHOT_BASE_OPTIONS.map(o => (
                <SelectItem key={o} value={o}>
                  {o}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </FilterField>
        <FilterField label="Source">
          <SimpleSelect
            value={source}
            onValueChange={setSource}
            options={SOURCE_OPTIONS}
          />
        </FilterField>
        <FilterField label="Season">
          <SimpleSelect
            value={season}
            onValueChange={setSeason}
            options={seasonOptions}
            loading={seasonsLoading}
          />
        </FilterField>
        <FilterField label="Event">
          <SimpleSelect
            value={event}
            onValueChange={setEvent}
            options={EVENT_OPTIONS}
          />
        </FilterField>
        <FilterField label="Division">
          <IdSelect
            value={divisionId}
            onValueChange={handleDivisionChange}
            options={divisionOptions}
            loading={divisionsLoading}
          />
        </FilterField>
        <FilterField label="Department">
          <IdSelect
            value={departmentId}
            onValueChange={handleDepartmentChange}
            options={departmentOptions}
            loading={departmentsLoading}
          />
        </FilterField>
        <FilterField label="Class">
          <IdSelect
            value={classId}
            onValueChange={handleClassChange}
            options={classOptions}
            loading={classesLoading}
            disabled={!departmentId}
          />
        </FilterField>
        <FilterField label="SubClass">
          <IdSelect
            value={subclassId}
            onValueChange={setSubclassId}
            options={subclassOptions}
            loading={subclassesLoading}
            disabled={!classId}
          />
        </FilterField>
        <FilterField label="DC">
          <SimpleSelect
            value={dc}
            onValueChange={setDc}
            options={DC_OPTIONS}
            formatOption={formatDistributionCenterName}
          />
        </FilterField>
      </div>

      {/* Chart + glossary panel */}
      <div className="grid grid-cols-[1fr_320px] gap-4">
        <StoreCubeCartonUpdateChart bars={chartBars} />

        <div className="flex flex-col gap-3 text-[11px] leading-tight text-muted-foreground">
          <div>
            <p className="font-semibold text-foreground">
              Average Metric per Store:
            </p>
            <p>
              Average selected metric per store by flow category and fiscal
              week. Use this to spot shifts in capacity demand across Core vs
              SEA (On Time / Rollover).
            </p>
          </div>

          <div>
            <p className="font-semibold text-foreground">Flow Categories:</p>
            <ul className="list-disc space-y-1 pl-4">
              <li>
                <span className="font-semibold">Core:</span> base replenishment
                volume, non-seasonal products (item season = NONE or CORE).
              </li>
              <li>
                <span className="font-semibold">SEA On Time:</span> Seasonal PO
                projected to reach stores on or before its in-stock date.
              </li>
              <li>
                <span className="font-semibold">SEA Rollover:</span> Seasonal PO
                projected to miss its in-stock date.
              </li>
            </ul>
          </div>

          <div>
            <p className="mb-1 font-semibold text-foreground">Metric</p>
            <RadioGroup value={metric} onValueChange={setMetric}>
              {METRIC_OPTIONS.map(option => (
                <div key={option} className="flex items-center gap-2">
                  <RadioGroupItem
                    value={option}
                    id={`metric-${option}`}
                    className="cursor-pointer"
                  />
                  <Label
                    htmlFor={`metric-${option}`}
                    className="cursor-pointer font-normal text-foreground"
                  >
                    {option}
                  </Label>
                </div>
              ))}
            </RadioGroup>
          </div>
        </div>
      </div>

      <StoreCubeCartonUpdateTable data={totalsByWeek} />
    </div>
  )
}
